import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Restaurant } from "@/lib/types";
import { debounce } from "@/lib/utils";
import { useFilterStore } from "@/lib/FilterStore";
import { loadGoogleMapsApi } from "@/lib/googleMaps";
import { useAnalytics } from "@/hooks/useAnalytics";

declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

interface SearchBarProps {
  className?: string;
  defaultLocation?: string;
}

interface ConfigResponse {
  googleMapsApiKey: string;
}

const SearchBar = ({ className = "", defaultLocation = "" }: SearchBarProps) => {
  const isDev = import.meta.env.DEV;
  const [searchQuery, setSearchQuery] = useState("");
  const [locationQuery, setLocationQuery] = useState(defaultLocation || "");
  const [, setLocation] = useLocation();
  const { track } = useAnalytics();
  const [userCoordinates, setUserCoordinates] = useState<{lat: number, lng: number} | null>(null);
  const locationInputRef = useRef<HTMLInputElement>(null); // Mobile
  const desktopLocationInputRef = useRef<HTMLInputElement>(null); // Desktop
  const [searchResults, setSearchResults] = useState<Restaurant[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  
  // Zugriff auf den FilterStore für die zentrale Zustandsverwaltung
  const filterStore = useFilterStore();

  // Laden der Konfiguration vom Server (reserved for future use)
  const { data: _config } = useQuery<ConfigResponse>({
    queryKey: ["/api/config"],
    staleTime: Infinity,
  });
  
  // Security: Niemals API Keys in die Konsole loggen (auch nicht gekürzt).
  
  // Live-Suche: Restaurants suchen
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append("q", query);
      // Füge Parameter hinzu, um nur im Namen zu suchen (für bessere Live-Suchergebnisse)
      params.append("nameOnly", "true");
      const url = `/api/restaurants/search?${params.toString()}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Fehler bei der Suche: ${response.status}`);
      }
      
      const results = await response.json();
      
      if (Array.isArray(results)) {
        setSearchResults(results);
      } else {
        console.error("Unerwartetes Format der Suchergebnisse:", results);
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Fehler bei der Restaurantsuche:", error);
      setSearchResults([]);
    }
  }, []);
  
  // Debounce-Funktion für Live-Suche
  const debouncedSearch = useCallback(
    debounce((query: string) => performSearch(query), 300),
    [performSearch]
  );
  
  // Aktualisiere Suchergebnisse, wenn sich die Suchanfrage ändert
  useEffect(() => {
    debouncedSearch(searchQuery);
    setShowResults(searchQuery.length >= 2);
  }, [searchQuery, debouncedSearch]);
  
  // Klick außerhalb der Suchergebnisse schließt diese
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current && 
        !searchResultsRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Google Maps API laden
  useEffect(() => {
    if (isDev) console.log("🚀 SearchBar: Lade Google Maps API");
    loadGoogleMapsApi()
      .then(() => {
        if (isDev) console.log("✅ SearchBar: Google Maps API geladen");
        setIsGoogleMapsLoaded(true);
      })
      .catch(error => {
        // In Prod nicht als Error spammen (AdBlock etc. ist erwartbar)
        if (isDev) {
          console.error('❌ SearchBar: Fehler beim Laden der Google Maps API:', error);
        } else {
          console.warn('Google Maps API konnte nicht geladen werden (möglicherweise blockiert).');
        }
      });
  }, []);

  // Autocomplete initialisieren wenn API geladen ist und Refs verfügbar sind
  useEffect(() => {
    if (isDev) {
      console.log("🔄 SearchBar: useEffect - isGoogleMapsLoaded:", isGoogleMapsLoaded);
      console.log("📍 Desktop locationInputRef:", !!desktopLocationInputRef.current);
      console.log("📍 Mobile locationInputRef:", !!locationInputRef.current);
    }

    if (isGoogleMapsLoaded) {
      // Desktop Autocomplete
      if (desktopLocationInputRef.current) {
        if (isDev) console.log("🎯 SearchBar: Initialisiere Desktop Autocomplete");
        setupAutocomplete(desktopLocationInputRef.current, 'desktop');
      }
      // Mobile Autocomplete
      if (locationInputRef.current) {
        if (isDev) console.log("🎯 SearchBar: Initialisiere Mobile Autocomplete");
        setupAutocomplete(locationInputRef.current, 'mobile');
      }
    }
  }, [isGoogleMapsLoaded, desktopLocationInputRef.current, locationInputRef.current]);


  const handleSearch = () => {
    // Generate search URL with query parameters
    const searchParams = new URLSearchParams();
    
    // Zuerst die Filter-Zustände aktualisieren
    if (searchQuery) {
      filterStore.setQuery(searchQuery);
    }
    
    if (locationQuery && userCoordinates) {
      filterStore.setLocation({
        label: locationQuery,
        lat: userCoordinates.lat,
        lng: userCoordinates.lng,
        source: 'manual'
      });
    } else if (locationQuery) {
      filterStore.setLocation({ label: locationQuery, source: 'manual' });
    } else if (userCoordinates) {
      filterStore.setLocation({
        label: '',
        lat: userCoordinates.lat,
        lng: userCoordinates.lng,
        source: 'geolocate'
      });
    }
    
    // Behalte bestehende Filter-Parameter
    if (window.location.pathname === '/restaurants') {
      const currentParams = new URLSearchParams(window.location.search);
      // Filter-Parameter weitergeben
      ['openNow', 'deliveryOnly', 'cuisines', 'price', 'rating', 'skills', 'distance'].forEach(param => {
        if (currentParams.has(param)) {
          searchParams.append(param, currentParams.get(param)!);
        }
      });
    }
    
    // Neue Suchparameter hinzufügen
    if (searchQuery) {
      searchParams.append("q", searchQuery);
    }
    
    if (locationQuery) {
      searchParams.append("location", locationQuery);
    }
    
    // Koordinaten nur hinzufügen, wenn wir einen Standort haben, aber keine formatierte Adresse
    if (userCoordinates && !locationQuery) {
      searchParams.append("lat", userCoordinates.lat.toString());
      searchParams.append("lng", userCoordinates.lng.toString());
    }

    // Log zur Fehlersuche
    if (isDev) {
      console.log("Suche mit folgenden Parametern:", {
        query: searchQuery,
        location: locationQuery,
        coordinates: userCoordinates,
        filterStoreState: {
          location: filterStore.location
        }
      });
    }

    // Track search_submit
    track({
      event_name: 'search_submit',
      metadata: {
        query: searchQuery,
        location: locationQuery,
      },
    });
    
    // Navigate to restaurants page with search parameters
    setLocation(`/restaurants?${searchParams.toString()}`);
  };

  // Google Places Autocomplete initialisieren (für beide Input-Felder)
  const setupAutocomplete = (inputElement: HTMLInputElement, type: 'desktop' | 'mobile') => {
    if (isDev) {
      console.log(`🔍 setupAutocomplete aufgerufen für ${type}`);
      console.log("🗺️ window.google?.maps?.places:", !!window.google?.maps?.places);
    }

    if (!window.google?.maps?.places) {
      if (isDev) console.warn("❌ Google Maps Places API nicht verfügbar");
      return;
    }

    try {
      if (isDev) console.log(`✅ Initialisiere Google Places Autocomplete für ${type}`);

      const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
        types: ["geocode", "establishment"],
        componentRestrictions: { country: "de" }
      });

      if (isDev) console.log(`✅ Autocomplete erfolgreich erstellt für ${type}:`, autocomplete);

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (isDev) console.log(`📍 Place changed in SearchBar (${type}):`, place);

        if (!place.geometry) {
          if (isDev) console.warn("⚠️ Kein geometry gefunden für:", place);
          return;
        }

        // Update location query with selected place name
        setLocationQuery(place.formatted_address || place.name);

        // Store coordinates for search
        const coordinates = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setUserCoordinates(coordinates);

        // Aktualisiere auch den FilterStore mit Label + Koordinaten
        filterStore.setLocation({
          label: place.formatted_address || place.name || '',
          lat: coordinates.lat,
          lng: coordinates.lng,
          source: 'google'
        });

        // Automatisch zur Restaurant-Liste navigieren
        setTimeout(() => {
          handleSearch();
        }, 300);
      });
    } catch (error) {
      console.error(`❌ Fehler beim Einrichten der Autocomplete-Funktion für ${type}:`, error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Aktuelle Geolocation ermitteln (ohne störende Modals)
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      console.log("Geolocation wird von diesem Browser nicht unterstützt.");
      return;
    }

    setLocationQuery("Standort wird ermittelt...");
    
    // UX: HighAccuracy kann je nach Device sehr langsam sein → default false, kurzer Timeout
    const options: PositionOptions = {
      enableHighAccuracy: false,
      timeout: 8000,
      maximumAge: 300000,
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        if (isDev) console.log("Standort erfolgreich ermittelt:", lat, lng);
        
        // Koordinaten setzen
        const coordinates = { lat, lng };
        setUserCoordinates(coordinates);
        filterStore.setLocation({
          label: '',
          lat: coordinates.lat,
          lng: coordinates.lng,
          source: 'geolocate'
        });
        
        // Geocoding für Adresse (optional; darf den Flow nicht blockieren – wichtig für uBlock/Privacy)
        try {
          if (window.google?.maps) {
            const geocoder = new window.google.maps.Geocoder();
            const response = await geocoder.geocode({
              location: { lat, lng }
            });
            
            if (response.results && response.results[0]) {
              const address = response.results[0].formatted_address;
              setLocationQuery(address);
              filterStore.setLocation({
                label: address,
                lat,
                lng,
                source: 'google'
              });
            } else {
              setLocationQuery("Mein Standort");
              filterStore.setLocation({
                label: "Mein Standort",
                lat,
                lng,
                source: 'geolocate'
              });
            }
          } else {
            setLocationQuery("Mein Standort");
            filterStore.setLocation({
              label: "Mein Standort",
              lat,
              lng,
              source: 'geolocate'
            });
          }
        } catch (error) {
          if (isDev) console.error("Geocoding fehlgeschlagen:", error);
          setLocationQuery("Mein Standort");
          filterStore.setLocation({
            label: "Mein Standort",
            lat,
            lng,
            source: 'geolocate'
          });
        }
        
        // Automatisch zur Restaurant-Liste navigieren, wenn Standort ermittelt wurde
        // (auch ohne Suchbegriff, da Standort allein ausreicht)
        setTimeout(() => {
          handleSearch();
        }, 500); // Kurze Verzögerung für bessere UX
      },
      (error) => {
        console.error("Geolocation-Fehler:", error);
        setLocationQuery("");
        
        // Einfache Fehlermeldung ohne Modal
        let errorMessage = "Standort konnte nicht ermittelt werden.";
        switch(error.code) {
          case 1: // PERMISSION_DENIED
            errorMessage = "Standortzugriff wurde verweigert.";
            break;
          case 2: // POSITION_UNAVAILABLE
            errorMessage = "Standortinformationen sind nicht verfügbar.";
            break;
          case 3: // TIMEOUT
            errorMessage = "Standortanfrage ist abgelaufen.";
            break;
        }
        console.log(errorMessage);
      },
      options
    );
  };

  // Funktion zum Auswählen eines Suchergebnisses
  const selectSearchResult = (restaurant: Restaurant) => {
    setSearchQuery(restaurant.name);
    setShowResults(false);
    // Verwende Slug, wenn verfügbar, sonst die ID
    setLocation(`/restaurants/${restaurant.slug || restaurant.id}`);
  };

  return (
    <div className={`max-w-5xl mx-auto ${className}`}>
      {/* Leichte Premium Glass Search Card */}
      <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-colors duration-300">
        <div className="p-4 md:p-6">
          {/* Desktop: Horizontale Search-Bar */}
          <div className="hidden lg:flex lg:flex-row gap-4 items-end">
            {/* Dominant: Suchfeld für Essen */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="fas fa-utensils text-gray-400 text-base"></i>
              </div>
              <input
                type="text"
                placeholder="Restaurant oder Küche suchen..."
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white transition-colors duration-300 text-neutral-dark placeholder-gray-500 text-base font-medium hover:shadow-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* Sekundär: Standort mit integriertem Location-Button */}
            <div className="w-80 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <i className="fas fa-map-marker-alt text-gray-400 text-base"></i>
              </div>
              <input
                ref={desktopLocationInputRef}
                type="text"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder="Standort eingeben..."
                className="w-full pl-12 pr-16 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white transition-colors duration-300 text-neutral-dark placeholder-gray-500 text-base font-medium hover:shadow-md"
                onKeyDown={handleKeyDown}
              />
              {/* Mein Standort Icon-Button im Location-Feld */}
              <button
                onClick={getCurrentLocation}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary-dark rounded-lg transition-colors duration-200 group"
                title="Mein Standort verwenden"
              >
                <i className="fas fa-crosshairs text-sm group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>

            {/* Primary CTA Button - vereinheitlicht */}
            <button
              onClick={() => {
                if (locationQuery || searchQuery || userCoordinates) {
                  handleSearch();
                } else {
                  setLocation('/restaurants');
                }
              }}
              disabled={!locationQuery && !searchQuery && !userCoordinates}
              className="bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-semibold py-4 px-8 rounded-xl transition-colors duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center gap-3 text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none"
            >
              <i className="fas fa-search text-lg"></i>
              <span>Restaurants finden</span>
            </button>
          </div>

          {/* Mobile: Logisch gestapelte Inputs */}
          <div className="lg:hidden space-y-4">
            {/* Dominant: Suchfeld */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="fas fa-utensils text-gray-400 text-base"></i>
              </div>
              <input
                type="text"
                placeholder="Restaurant oder Küche suchen..."
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white transition-colors duration-300 text-neutral-dark placeholder-gray-500 text-base font-medium"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* Sekundär: Standort mit Location-Button */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <i className="fas fa-map-marker-alt text-gray-400 text-base"></i>
              </div>
              <input
                type="text"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder="Standort eingeben..."
                className="w-full pl-12 pr-16 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/20 focus:border-primary focus:bg-white transition-colors duration-300 text-neutral-dark placeholder-gray-500 text-base font-medium"
                onKeyDown={handleKeyDown}
              />
              <button
                onClick={getCurrentLocation}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-3 bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary-dark rounded-lg transition-colors duration-200 group"
                title="Mein Standort verwenden"
              >
                <i className="fas fa-crosshairs text-base group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>

            {/* Primary CTA - bleibt klar und groß */}
            <button
              onClick={() => {
                if (locationQuery || searchQuery || userCoordinates) {
                  handleSearch();
                } else {
                  setLocation('/restaurants');
                }
              }}
              disabled={!locationQuery && !searchQuery && !userCoordinates}
              className="w-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-semibold py-5 px-6 rounded-xl transition-colors duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none"
            >
              <i className="fas fa-search text-xl"></i>
              <span>Restaurants finden</span>
              <i className="fas fa-arrow-right text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Live-Suchergebnisse */}
      {showResults && searchResults.length > 0 && (
        <div 
          ref={searchResultsRef}
          className="mt-4 bg-white/95 backdrop-blur-lg rounded-xl shadow-lg border border-white/20 max-h-80 overflow-y-auto"
        >
          <div className="p-2">
            {searchResults.map((restaurant) => (
              <div
                key={restaurant.id}
                className="p-4 hover:bg-gray-50 cursor-pointer flex items-center rounded-lg transition-colors"
                onClick={() => selectSearchResult(restaurant)}
              >
                <div className="w-12 h-12 rounded-xl mr-4 bg-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
                  {restaurant.imageUrl ? (
                    <img 
                      src={restaurant.imageUrl} 
                      alt={restaurant.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-500 text-xl font-semibold">
                      {restaurant.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-neutral-dark text-lg">{restaurant.name}</div>
                  <div className="text-sm text-gray-600 flex items-center mt-1">
                    <span className="flex items-center mr-3">
                      <i className="fas fa-star text-yellow-400 text-sm mr-1"></i>
                      {(restaurant.rating ?? 0).toFixed(1)}
                    </span>
                    <span className="flex items-center">
                      <i className="fas fa-map-marker-alt text-gray-400 text-sm mr-1"></i>
                      {restaurant.city}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
